import React from 'react';
/**
 * SuccessContent – the confirmation after a message is sent.
 *
 * "Message sent" on the shared anatomy, naming the reply address when the page
 * holds it so a typo is visible while the reader can still act on it. The
 * footer keeps offering the call, inside this dialog. A manual review confirms
 * the request and offers nothing else: there is no report to talk through.
 *
 * @module
 */

import { motion } from 'framer-motion';

import type { SuccessContentProps } from './BookingModal.types';
import { CallLink, ModalFooter, ModalHeader } from './BookingModal.anatomy';
import { BOOKING_SHORT_CALL_LABEL } from '@/lib/conversion-copy';

export function SuccessContent({ manualReview, replyEmail, onBookCall }: SuccessContentProps): React.ReactElement {
  return (
    <>
      <ModalHeader title={manualReview ? 'Review request received' : 'Message sent'} focusable />
      <motion.div
        className="flex items-start gap-3 px-5 py-6 sm:px-8"
        role="status"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <motion.span
          className="grid h-7 w-7 flex-none place-items-center rounded-full bg-green-100 text-green-600"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
            <motion.path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.4, delay: 0.15, ease: 'easeOut' }}
            />
          </svg>
        </motion.span>
        <p className="mt-0.5 text-base text-slate-700">
          {manualReview ? (
            'We’ve got your request and will follow up by email.'
          ) : replyEmail ? (
            <>
              Nathan will reply to <strong className="font-medium text-slate-900 [overflow-wrap:anywhere]">{replyEmail}</strong> within 24
              hours.
            </>
          ) : (
            'Nathan will reply within 24 hours.'
          )}
        </p>
      </motion.div>
      {!manualReview && (
        <ModalFooter>
          <CallLink label={BOOKING_SHORT_CALL_LABEL} onClick={onBookCall} />
        </ModalFooter>
      )}
    </>
  );
}
